function finiteNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function timestampMs(value) {
  if (typeof value === 'string' && !/^\d+(\.\d+)?$/.test(value.trim())) {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  const number = finiteNumber(value)
  if (number === null) return null
  if (number < 100_000_000) return number * 60_000
  if (number < 100_000_000_000) return number * 1000
  return number
}

function impliedProbability(american) {
  const number = finiteNumber(american)
  if (number === null) return null
  if (number >= 100) return 100 / (number + 100)
  if (number <= -100) return Math.abs(number) / (Math.abs(number) + 100)
  return null
}

function unwrap(payload, historyKey) {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  if (Array.isArray(payload[historyKey])) return payload[historyKey]
  for (const key of ['samples', 'history', 'points', 'data']) {
    if (Array.isArray(payload[key])) return payload[key]
    if (Array.isArray(payload[key]?.[historyKey])) return payload[key][historyKey]
  }
  return []
}

export function buildHistoryKey(row) {
  return [row?.book, row?.event, row?.bet_type, row?.selection, row?.line ?? 'None'].join('|')
}

export function normalizeHistorySamples(payload, historyKey) {
  return unwrap(payload, historyKey).map((sample) => {
    const time = Array.isArray(sample) ? sample[0] : sample.time ?? sample.ts ?? sample.timestamp ?? sample.epoch_minute
    const odds = Array.isArray(sample) ? sample[1] : sample.american ?? sample.odds ?? sample.price
    return { time: timestampMs(time), odds: finiteNumber(odds), raw: sample }
  }).filter((sample) => sample.time !== null && sample.odds !== null)
    .sort((left, right) => left.time - right.time)
}

function dimensions(samples) {
  const values = samples.map((sample) => impliedProbability(sample.odds)).filter((value) => value !== null)
  if (values.length === 0) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  return { min, max, span: Math.max(0.005, max - min) }
}

function pointFor(sample, index, samples, range) {
  const probability = impliedProbability(sample.odds)
  const x = 28 + (samples.length === 1 ? 0.5 : index / (samples.length - 1)) * 584
  const y = 16 + ((range.max - probability) / range.span) * 142
  return { ...sample, probability, x, y }
}

function timeLabel(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(value))
}

function oddsLabel(value) {
  return value > 0 ? `+${value}` : String(value)
}

export default function LiveOddsVNextHistoryChart({ payload, historyKey }) {
  const samples = normalizeHistorySamples(payload, historyKey)
  const chartSamples = samples.filter((sample) => impliedProbability(sample.odds) !== null)
  const suspiciousSamples = samples.filter((sample) => impliedProbability(sample.odds) === null)
  const range = dimensions(chartSamples)
  if (samples.length === 0) return <div className="lovx-evidence-state">No epoch-minute price samples for this exact offer.</div>
  if (!range) return <div className="lovx-evidence-state warn">{samples.length} raw sample{samples.length === 1 ? '' : 's'} retained, but none contain valid American odds for implied-probability charting.</div>
  const points = chartSamples.map((sample, index) => pointFor(sample, index, chartSamples, range))
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ')
  return (
    <div className="lovx-history-chart">
      <svg viewBox="0 0 640 190" role="img" aria-label={`${samples.length} stored odds history samples`}>
        <defs><linearGradient id="lovxHistoryFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#28d7f2" stopOpacity=".35" /><stop offset="1" stopColor="#28d7f2" stopOpacity="0" /></linearGradient></defs>
        {[16, 87, 158].map((y) => <line key={y} x1="28" x2="612" y1={y} y2={y} className="lovx-chart-grid" />)}
        <path d={`${path} L ${points.at(-1).x} 158 L ${points[0].x} 158 Z`} className="lovx-chart-area" />
        <path d={path} className="lovx-chart-line" />
        {points.map((point, index) => <circle key={`${point.time}|${point.odds}|${index}`} cx={point.x} cy={point.y} r="3"><title>{timeLabel(point.time)} · {oddsLabel(point.odds)} · {(point.probability * 100).toFixed(2)}% implied</title></circle>)}
        <text x="28" y="181">{timeLabel(points[0].time)}</text><text x="612" y="181" textAnchor="end">{timeLabel(points.at(-1).time)}</text>
      </svg>
      <div className="lovx-history-readout">
        <span>{samples.length.toLocaleString()} raw samples</span>
        <span>First {oddsLabel(chartSamples[0].odds)}</span>
        <span>Latest {oddsLabel(chartSamples.at(-1).odds)}</span>
        <span>{((impliedProbability(chartSamples.at(-1).odds) - impliedProbability(chartSamples[0].odds)) * 100).toFixed(2)} pp</span>
        {suspiciousSamples.length > 0 && <span className="lovx-history-warning">{suspiciousSamples.length} suspicious raw sample{suspiciousSamples.length === 1 ? '' : 's'} retained; not plotted</span>}
      </div>
    </div>
  )
}
